import { gql } from "@apollo/client";
import { Suspense, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";
import styled from "styled-components";

import useCanEditBrand from "../../../../clients/users/useCanEditBrand";
import * as UI from "../../../../modules/ui-kit";
import {
    useUnsavedChangesEditor,
    type UnsavedChangesContext,
} from "../../../../modules/unsaved-changes";
import Pricelists from "../../clients/Pricelists.graphql";
import useDeletePricelist from "../../clients/useDeletePricelist";
import usePricelists, {
    Currency,
    IPricelist,
} from "../../clients/usePricelists";
import useRecordPrices from "../../clients/useRecordPrices";
import useUpdatePricelist from "../../clients/useUpdatePricelist";
import CountrySelect from "../../components/CountrySelect";
import { parseXlsx, parsePriceRows, assemblePrices, TEMPLATE_URL } from "../../lib/importXlsx";
import { resolveProductIds } from "../../lib/resolveProducts";

import type { PricelistsOutletContext } from "../Pricelists";

const Styled = styled.section`
    display: flex;
    flex-direction: column;
    gap: 24px;

    header {
        align-items: center;
        display: flex;
        gap: 16px;
        justify-content: space-between;
    }

    header span {
        font-size: 2.4rem;
    }

    header .deprecated {
        color: #d00;
        font-size: 1rem;
        font-weight: bold;
    }

    header .default {
        color: #4dabf7;
        font-size: 1rem;
        font-weight: bold;
    }

    form {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    label {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 0.9rem;
    }

    input {
        padding: 6px 8px;
        font-size: 1rem;
        border: 1px solid #ccc;
    }

    .actions {
        display: flex;
        gap: 12px;
        margin-top: 8px;
    }

    .error {
        color: red;
        font-size: 0.85rem;
    }
    .info {
        color: #333;
        font-size: 0.85rem;
    }

    button {
        cursor: pointer;
        padding: 6px 14px;
    }
    button.danger {
        color: red;
        border-color: red;
    }
`;

const CURRENCY_OPTIONS: { label: string; value: Currency }[] = [
    { label: "EUR", value: "EUR" },
    { label: "CZK", value: "CZK" },
    { label: "CHF", value: "CHF" },
    { label: "RON", value: "RON" },
    { label: "RSD", value: "RSD" },
    { label: "HUF", value: "HUF" },
    { label: "BGN", value: "BGN" },
];

export default function Pricelist() {
    return (
        <Styled>
            <Suspense fallback={<span>loading</span>}>
                <PricelistDetail />
            </Suspense>
        </Styled>
    );
}

function PricelistDetail() {
    const { brandID, pricelistID } = useParams();
    const { data, refetch } = usePricelists(brandID);
    const guard = useOutletContext<PricelistsOutletContext>();
    const pricelist = data.pricelists.find((p) => p.id === pricelistID);

    if (!pricelist) return <span>not found</span>;

    return (
        <PricelistEditor
            key={pricelist.id}
            pricelist={pricelist}
            onRefetch={refetch}
            guard={guard}
        />
    );
}

function PricelistEditor({
    pricelist,
    onRefetch,
    guard,
}: {
    pricelist: IPricelist;
    onRefetch: () => Promise<unknown>;
    guard: UnsavedChangesContext;
}) {
    const { brandID } = useParams();
    const navigate = useNavigate();
    const canEdit = useCanEditBrand(brandID);

    const markDirty = () => {
        guard.isDirtyRef.current = true;
    };

    const [name, setName] = useState(pricelist.name[0]?.text ?? "");
    const [currency, setCurrency] = useState<Currency>(
        pricelist.currency ?? "EUR",
    );
    const [countries, setCountries] = useState<string[]>(
        pricelist.countries ?? [],
    );
    const country = countries[0] ?? "";
    const [validFrom, setValidFrom] = useState(
        pricelist.valid?.from?.slice(0, 10) ?? "",
    );
    const [validTo, setValidTo] = useState(
        pricelist.valid?.to?.slice(0, 10) ?? "",
    );
    const [archived, setArchived] = useState(pricelist.archived ?? false);
    const [isDefault, setIsDefault] = useState(
        !!country && (pricelist.defaultForCountries ?? []).includes(country),
    );
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const busy = saving || importing || deleting;
    const fileRef = useRef<HTMLInputElement>(null);

    const [updatePricelist] = useUpdatePricelist();
    const [deletePricelist] = useDeletePricelist();
    const [recordPrices] = useRecordPrices();

    async function handleSave(e?: React.FormEvent): Promise<boolean> {
        e?.preventDefault();
        setError(null);
        setInfo(null);

        if (!country) {
            setError("Country is required.");
            return false;
        }

        setSaving(true);

        try {
            await updatePricelist({
                variables: {
                    id: pricelist.id,
                    pricelist: {
                        name: [{ language: "en", text: name.trim() }],
                        currency,
                        countries,
                        valid:
                            validFrom || validTo
                                ? {
                                    from: validFrom || null,
                                    to: validTo || null,
                                }
                                : null,
                        archived,
                        defaultForCountries:
                            isDefault && country ? [country] : [],
                    },
                },
            });
            guard.isDirtyRef.current = false;
            setInfo("Saved.");
            setTimeout(() => setInfo(null), 5000);
            onRefetch();
            return true;
        } catch (e) {
            setError(e.message);
            return false;
        } finally {
            setSaving(false);
        }
    }

    useUnsavedChangesEditor(guard, () => handleSave());

    async function handleUpload() {
        const file = fileRef.current?.files?.[0];
        if (!file) {
            setError("Select an XLSX file");
            return;
        }

        if (
            !confirm(
                "This will overwrite all existing prices for this pricelist. Continue?",
            )
        )
            return;

        setError(null);
        setInfo(null);
        setImporting(true);

        try {
            const rows = await parseXlsx(file);
            const { rows: parsed, failed } = parsePriceRows(rows);

            const { idByValue } = await resolveProductIds(
                parsed.map((r) => r.identifier),
                brandID,
            );
            const { prices, notFound } = assemblePrices(pricelist.id, parsed, idByValue);

            if (prices.length === 0) {
                setError("No products matched the identifiers in column A for this brand.");
                return;
            }
            if (notFound.length > 0 && !confirm(
                `${notFound.length} of ${parsed.length} products were not found. Import the remaining ${prices.length}?`,
            )) return;

            await recordPrices({ variables: { prices } });

            setInfo(`Imported: ${prices.length} rows OK, ${failed} invalid, ${notFound.length} not found.`);
            if (fileRef.current) fileRef.current.value = "";
            await onRefetch();
        } catch (e) {
            setError(e.message);
        } finally {
            setImporting(false);
        }
    }

    async function handleDelete() {
        if (!confirm(`Delete "${name}"? This also deletes all its prices.`))
            return;

        setDeleting(true);
        try {
            await deletePricelist({
                variables: { id: pricelist.id },
                refetchQueries: [{ query: gql(Pricelists), variables: { brandID } }],
                awaitRefetchQueries: true,
            });
            navigate(`/pricelists/${brandID}`);
        } catch (e) {
            setError(e.message);
            setDeleting(false);
        }
    }

    return (
        <>
            <header>
                <span style={archived ? { color: "#d00" } : undefined}>
                    Price List
                    {archived && <span className="deprecated"> · DEPRECATED</span>}
                    {isDefault && <span className="default"> · DEFAULT</span>}
                </span>
                {canEdit && (
                    <div className="actions">
                        <UI.Button
                            type="button"
                            onClick={handleDelete}
                            disabled={busy}
                            style={{
                                background: "red",
                                borderColor: "red",
                                color: "#FFF",
                            }}
                        >
                            Delete price list
                        </UI.Button>
                    </div>
                )}
            </header>
            <form onSubmit={handleSave}>
                <label>
                    Name
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            markDirty();
                        }}
                    />
                </label>
                <label>
                    Currency
                    <UI.Select
                        value={currency}
                        onSelect={(v) => {
                            setCurrency(v as Currency);
                            markDirty();
                        }}
                        options={CURRENCY_OPTIONS}
                    />
                </label>
                <label>
                    Country
                    <CountrySelect
                        value={countries}
                        onChange={(v) => {
                            setCountries(v);
                            markDirty();
                        }}
                        disabled={busy}
                    />
                </label>
                <label>
                    Valid from
                    <input
                        type="date"
                        value={validFrom}
                        onChange={(e) => {
                            setValidFrom(e.target.value);
                            markDirty();
                        }}
                    />
                </label>
                <label>
                    Valid to
                    <input
                        type="date"
                        value={validTo}
                        onChange={(e) => {
                            setValidTo(e.target.value);
                            markDirty();
                        }}
                    />
                </label>
                <label>
                    Archived
                    <UI.SegmentedControl
                        disabled={!canEdit || busy}
                        onChange={(v) => {
                            setArchived(v);
                            markDirty();
                        }}
                        options={[
                            { label: "Archived", value: true },
                            { label: "Not archived", value: false },
                        ]}
                        value={archived}
                    />
                </label>
                <label>
                    Default price list for this country
                    <UI.SegmentedControl
                        disabled={!canEdit || busy || !country}
                        onChange={(v) => {
                            setIsDefault(v);
                            markDirty();
                        }}
                        options={[
                            { label: "Default", value: true },
                            { label: "Not default", value: false },
                        ]}
                        value={isDefault}
                    />
                </label>
                {error && <span className="error">{error}</span>}
                {info && <span className="info">{info}</span>}
                {canEdit && (
                    <div className="actions">
                        <UI.Button
                            type="submit"
                            $appearance="primary"
                            disabled={busy}
                        >
                            {saving ? "Saving…" : "Save"}
                        </UI.Button>
                    </div>
                )}
            </form>

            {canEdit && (
                <div>
                    <label
                        style={{ display: "flex", flexDirection: "column", gap: 8 }}
                    >
                        Upload updated price list (upserts all prices)
                        <input type="file" accept=".xlsx" ref={fileRef} />
                    </label>
                    <div className="actions" style={{ marginTop: 8 }}>
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={busy}
                        >
                            {importing ? "Importing…" : "Import"}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.open(TEMPLATE_URL, "_blank")}
                        >
                            Download template
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
